<template>
  <div class="root-box">
    <div class="query-container space-between">
      <div class="query-left">
        <el-cascader v-model="queryForm.problemIdPath" :options="cascaderOptions" :props="cascaderProps" filterable
          clearable placeholder="请选择问题" class="query-input" style="width: 300px"
          @change="handleQueryProblemChange"></el-cascader>
        <el-button type="primary" :size="size" @click="query" icon="el-icon-search">查询</el-button>
      </div>
      <div class="query-right">
        <el-button type="primary" :size="size" @click="openAdd()" icon="el-icon-plus">新增配置</el-button>
      </div>
    </div>

    <el-table :data="list" stripe max-height="600" v-loading="loading">
      <el-table-column label="序号" type="index" width="80"></el-table-column>
      <el-table-column prop="problemTitle" label="问题标题"></el-table-column>
      <el-table-column label="是否需要客服关注">
        <template slot-scope="scope">
          <span>{{ scope.row.needKfFocus ? "是" : "否" }}</span>
        </template>
      </el-table-column>
      <el-table-column label="步骤数量">
        <template slot-scope="scope">
          <span>{{ scope.row.steps ? Object.keys(scope.row.steps).length : 0 }}</span>
        </template>
      </el-table-column>
      <el-table-column label="更新时间">
        <template slot-scope="scope">
          <span>{{ scope.row.updateTime | dateFormat("yyyy-MM-dd hh:mm:ss") }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)">编辑</el-button>
          <el-button type="text" size="small" @click="viewConfig(scope.row)">查看</el-button>
          <el-button type="text" size="small" @click="deleteConfig(scope.row.problemId)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 配置编辑对话框 -->
    <el-dialog :visible.sync="configVisible" :title="isEdit ? '编辑流程配置' : '新增流程配置'" :fullscreen="true"
      :close-on-click-modal="false" :before-close="handleClose" :append-to-body="true"
      custom-class="config-editor-dialog">
      <div class="config-editor">
        <div class="config-basic-info">
          <el-form :model="configForm" :size="size" label-width="100px" :rules="configRules" ref="configForm"
            :inline="true">
            <el-form-item label="问题" prop="problemId">
              <el-cascader v-model="configForm.problemIdPath" :options="cascaderOptions" :props="cascaderProps"
                @change="handleProblemChange" filterable placeholder="请选择问题" style="width: 300px"></el-cascader>
            </el-form-item>
            <el-form-item label="初始步骤ID" prop="initialStepId">
              <el-select v-model="configForm.initialStepId" filterable placeholder="请选择初始步骤" style="width: 200px">
                <el-option v-for="step in stepOptions" :key="step.stepId" :label="step.label"
                  :value="step.stepId"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="需要客服关注" prop="needKfFocus">
              <el-switch v-model="configForm.needKfFocus" @change="updateField('needKfFocus', $event)" active-text="是"
                inactive-text="否"></el-switch>
            </el-form-item>
          </el-form>
        </div>

        <div class="config-steps-container">
          <div class="steps-toolbar">
            <el-button type="info" size="small" @click="importFromJson" icon="el-icon-upload2">从JSON导入</el-button>
            <el-button type="success" size="small" @click="exportToJson" icon="el-icon-download">导出JSON</el-button>
          </div>

          <!-- 可视化流程图编辑器 -->
          <div class="flow-editor-wrapper">
            <flow-editor :steps="configForm.steps" :initial-step-id="configForm.initialStepId"
              @update="updateStepsFromFlow"></flow-editor>
          </div>
        </div>
      </div>

      <span slot="footer">
        <el-button type="success" :size="size" @click="saveConfig(true)">暂存</el-button>
        <el-button type="warning" :size="size" @click="saveConfig(false)">保存</el-button>
        <el-button type="info" :size="size" @click="handleClose">取 消</el-button>
      </span>
    </el-dialog>

    <!-- 查看配置对话框 -->
    <el-dialog :visible.sync="viewVisible" title="查看配置" width="80%" :close-on-click-modal="false" :append-to-body="true"
      :fullscreen="true" :z-index="2001">
      <pre class="json-viewer">{{ JSON.stringify(viewConfigData, null, 2) }}</pre>
      <span slot="footer">
        <el-button type="info" :size="size" @click="viewVisible = false">关 闭</el-button>
      </span>
    </el-dialog>

    <!-- JSON导入对话框 -->
    <el-dialog :visible.sync="importJsonVisible" title="从JSON导入配置" :close-on-click-modal="false" :append-to-body="true"
      height="600px">
      <el-form :model="importJsonForm" :rules="importJsonRules" ref="importJsonForm">
        <el-form-item prop="jsonStr">
          <el-input v-model="importJsonForm.jsonStr" type="textarea" :rows="15" placeholder="请粘贴JSON配置..."></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button type="primary" :size="size" @click="confirmImportJson">确定</el-button>
        <el-button type="info" :size="size" @click="importJsonVisible = false">取 消</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  listProblemFlowConfigs,
  saveOrUpdateProblemFlowConfig,
  deleteProblemFlowConfig,
  getProblemFlowConfigEnums,
  getSpecProblemList
} from "@/api/problem_flow_config";
import { mapState, mapGetters } from "vuex";
import FlowEditor from "./components/FlowEditor.vue";

export default {
  name: "ProblemFlowConfig",
  components: {
    FlowEditor
  },
  data() {
    return {
      loading: false,
      list: [],
      queryForm: {
        problemId: "",
        problemIdPath: [] // 级联选择器的路径数组
      },
      problemList: [], // 问题列表（带children结构）
      cascaderProps: {
        value: "id",
        label: "title",
        children: "children",
        emitPath: true, // 返回完整路径
        checkStrictly: false // 只能选择叶子节点
      },
      configVisible: false,
      isEdit: false,
      viewVisible: false,
      viewConfigData: null,
      importJsonVisible: false,
      importJsonForm: {
        jsonStr: ""
      },
      importJsonRules: {
        jsonStr: [{ required: true, message: "请输入JSON配置", trigger: "blur" }]
      },
      configForm: {
        problemId: "",
        problemTitle: "",
        initialStepId: "",
        steps: {}
      },
      configRules: {
        problemId: [{ required: true, message: "请选择问题", trigger: "change" }],
        initialStepId: [{ required: true, message: "请选择初始步骤ID", trigger: "blur" }]
      }
    };
  },
  computed: {
    ...mapState({
      size: (state) => state.btnSize
    }),
    ...mapGetters("problemFlowConfig", ["steps", "stepOptions", "fullConfig"]),
    stepIds() {
      return Object.keys(this.steps || {});
    },
    // 级联选择器的选项数据
    cascaderOptions() {
      return this.problemList || [];
    }
  },
  created() {
    this.loadEnums();
    this.loadProblemList();
    this.query();
  },
  methods: {
    loadEnums() {
      getProblemFlowConfigEnums().then((res) => {
        this.$store.dispatch("problemFlowConfig/setEnums", res.data.enums);
      });
    },
    loadProblemList() {
      getSpecProblemList().then((res) => {
        this.problemList = res.data || [];
      });
    },
    query() {
      this.loading = true;
      listProblemFlowConfigs(this.queryForm).then((res) => {
        this.list = res.data || [];
        this.loading = false;
      });
    },
    openAdd() {
      this.isEdit = false;
      this.configForm = {
        problemId: "",
        problemTitle: "",
        initialStepId: "",
        steps: {},
        problemIdPath: []
      };
      this.$store.dispatch("problemFlowConfig/initConfig", this.configForm);
      this.configVisible = true;
    },
    openEdit(row) {
      this.isEdit = true;
      this.configForm = JSON.parse(JSON.stringify(row));
      if (!this.configForm.steps) {
        this.configForm.steps = {};
      }
      // 根据 problemId 构建路径数组
      this.configForm.problemIdPath = this.buildPathById(this.configForm.problemId);
      this.$store.dispatch("problemFlowConfig/initConfig", this.configForm);
      this.configVisible = true;
    },
    viewConfig(row) {
      this.viewConfigData = row;
      this.viewVisible = true;
    },
    deleteConfig(problemId) {
      this.$confirm("确认删除该配置吗?", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        deleteProblemFlowConfig(problemId)
          .then((res) => {
            if (res.code === 200) {
              this.$message.success("删除成功");
              this.query();
            } else {
              this.$message.error(res.message || "删除失败");
            }
          })
          .catch((err) => {
            this.$message.error(err.message || "删除失败");
          });
      });
    },
    handleProblemChange(value) {
      // value 是级联选择器的路径数组，如 ['1', '1.1']
      if (!value || value.length === 0) {
        this.configForm.problemId = "";
        this.configForm.problemTitle = "";
        return;
      }
      // 取路径的最后一个值作为 problemId
      const problemId = value[value.length - 1];
      this.configForm.problemId = problemId;

      // 根据 problemId 查找对应的 title
      const selectedProblem = this.findProblemById(problemId);
      if (selectedProblem) {
        this.configForm.problemTitle = selectedProblem.title;
      }
    },
    handleQueryProblemChange(value) {
      // 查询条件的变化处理
      if (!value || value.length === 0) {
        this.queryForm.problemId = "";
        return;
      }
      // 取路径的最后一个值作为 problemId
      this.queryForm.problemId = value[value.length - 1];
    },
    // 根据 problemId 查找问题对象（支持父级和子级）
    findProblemById(problemId) {
      for (const group of this.problemList) {
        if (group.id === problemId) {
          return group;
        }
        if (group.children && group.children.length > 0) {
          const child = group.children.find((c) => c.id === problemId);
          if (child) {
            return child;
          }
        }
      }
      return null;
    },
    // 根据 problemId 构建路径数组
    buildPathById(problemId) {
      if (!problemId) {
        return [];
      }
      for (const group of this.problemList) {
        if (group.id === problemId) {
          // 如果父级没有子级，返回父级路径
          if (!group.children || group.children.length === 0) {
            return [group.id];
          }
          // 如果父级有子级，不应该选择父级，返回空
          return [];
        }
        if (group.children && group.children.length > 0) {
          const child = group.children.find((c) => c.id === problemId);
          if (child) {
            return [group.id, child.id];
          }
        }
      }
      return [];
    },
    updateStepsFromFlow(steps) {
      // 步骤数据已通过 Vuex 管理，这里只需要更新 configForm
      // 使用 nextTick 避免在 watch 中触发循环更新
      this.$nextTick(() => {
        // 只有当数据真的变化时才更新，避免循环
        const currentStepsStr = JSON.stringify(this.configForm.steps || {});
        const newStepsStr = JSON.stringify(steps || {});
        if (currentStepsStr !== newStepsStr) {
          this.configForm.steps = steps;
        }
      });
    },
    saveConfig(tempSave = false) {
      this.$refs.configForm.validate((valid) => {
        if (!valid) {
          return;
        }
        const currentSteps = this.steps;
        if (Object.keys(currentSteps).length === 0) {
          this.$message.warning("请至少添加一个步骤");
          return;
        }
        if (!currentSteps[this.configForm.initialStepId]) {
          this.$message.warning("初始步骤ID不存在");
          return;
        }
        const configToSave = {
          ...this.configForm,
          steps: currentSteps
        };
        saveOrUpdateProblemFlowConfig(configToSave).then((res) => {
          this.$message.success("保存成功");
          if (res.data && res.data.id) {
            this.configForm.id = res.data.id;
            this.isEdit = true;
          }
          if (!tempSave) {
            this.configVisible = false;
            this.$store.dispatch("problemFlowConfig/resetState");
            this.query();
          }
        });
      });
    },

    handleClose() {
      this.configVisible = false;
      this.$store.dispatch("problemFlowConfig/resetState");
      this.query();
    },
    importFromJson() {
      this.importJsonForm.jsonStr = "";
      this.importJsonVisible = true;
      this.$nextTick(() => {
        if (this.$refs.importJsonForm) {
          this.$refs.importJsonForm.clearValidate();
        }
      });
    },
    confirmImportJson() {
      this.$refs.importJsonForm.validate((valid) => {
        if (!valid) {
          return;
        }
        const jsonStr = this.importJsonForm.jsonStr.trim();
        if (!jsonStr) {
          this.$message.warning("请输入JSON配置");
          return;
        }
        try {
          const config = JSON.parse(jsonStr);
          const currentId = this.configForm.id;
          this.configForm = config;
          if (!this.configForm.steps) {
            this.configForm.steps = {};
          }
          if (this.isEdit) {
            this.configForm.id = currentId;
          } else {
            this.$delete(this.configForm, "id");
          }
          this.importJsonVisible = false;
          this.$message.success("导入成功");
        } catch (e) {
          this.$message.error("JSON格式错误: " + e.message);
        }
      });
    },
    exportToJson() {
      const jsonStr = JSON.stringify(this.configForm, null, 2);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `problem-${this.configForm.problemId || "new"}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style lang="less" scoped>
// 查询容器样式优化
.query-container {
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .query-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    .query-input {
      width: 200px;
    }
  }

  .query-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  // 响应式处理
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    .query-left,
    .query-right {
      width: 100%;
      justify-content: flex-start;
    }

    .query-left {
      margin-bottom: 12px;
    }

    .query-input {
      flex: 1;
      min-width: 150px;
    }
  }
}

// 全屏对话框内容样式优化
.config-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  margin: 0;

  .config-basic-info {
    flex-shrink: 0;
    margin-bottom: 12px;
    padding: 12px 16px;
    background: #f5f7fa;
    border-radius: 4px;
    border: 1px solid #e4e7ed;

    // 表单项样式优化
    /deep/ .el-form-item {
      margin-bottom: 0;
      margin-right: 20px;
    }
  }

  .config-steps-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0; // 允许 flex 子元素收缩
    margin-bottom: 0;

    .steps-toolbar {
      flex-shrink: 0;
      margin-bottom: 10px;
      display: flex;
      gap: 10px;
      justify-content: flex-end;
    }

    .flow-editor-wrapper {
      flex: 1;
      min-height: 0;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      overflow: hidden;
      background: #fff;
    }
  }
}

// 全屏对话框样式覆盖 - 只作用于配置编辑对话框
/deep/ .config-editor-dialog {
  .el-dialog__body {
    padding: 16px 20px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    height: calc(100vh - 110px); // 减去对话框头部和底部的高度
  }

  .el-dialog__header {
    padding: 12px 20px;
    border-bottom: 1px solid #e4e7ed;
  }

  .el-dialog__footer {
    padding: 12px 20px;
    border-top: 1px solid #e4e7ed;
    flex-shrink: 0;
  }
}

.json-viewer {
  background: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  max-height: 600px;
  overflow: auto;
  font-family: "Courier New", monospace;
  font-size: 12px;
  line-height: 1.5;
}
</style>
